import React, { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { CheckCircle2, AlertTriangle, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import OrganizationsRepository from "../repositories/OrganizationsRepository";
import ConfirmationModal from "../components/common/ConfirmationModal";
import MigrationQueueStatusPanel from "../components/Organizations/MigrationQueueStatusPanel";
import { UserRole } from "../models/User";
import { MigrationType, MigrationTypeLabels } from "../models/Statistics/MigrationType";
import { RecalculationTrigger } from "../models/Recalculation";
import {
    RECALCULATION_MIGRATION_TYPES,
    RECALCULATION_WARNINGS,
} from "../constants/RecalculationWarnings";
import { QUERY_KEYS } from "../constants/QueryKeys";
import DocsHelpLink from "@/components/Docs/DocsHelpLink";

interface RecalculateFilePrioritiesPageProps {
    userRole: UserRole;
    organizationName?: string;
    organizationId?: string;
}

const RecalculateFilePrioritiesPage: React.FC<
    RecalculateFilePrioritiesPageProps
> = ({ userRole, organizationName, organizationId }) => {
    const [selectedTypes, setSelectedTypes] = useState<MigrationType[]>([
        ...RECALCULATION_MIGRATION_TYPES,
    ]);
    const [reason, setReason] = useState<string>("");
    const [status, setStatus] = useState<
        "idle" | "loading" | "submitted" | "error"
    >("idle");
    const [resultMessage, setResultMessage] = useState<string | null>(null);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const [isConfirmModalOpen, setIsConfirmModalOpen] =
        useState<boolean>(false);

    const organizationsRepository = new OrganizationsRepository();
    const queryClient = useQueryClient();

    const isReasonValid = reason.trim().length >= 10;
    const canSubmit =
        selectedTypes.length > 0 &&
        isReasonValid &&
        !!organizationId &&
        status !== "loading" &&
        (userRole === UserRole.Admin || userRole === UserRole.Editor);

    const handleSubmit = async () => {
        setStatus("loading");
        setErrorMessage(null);
        setResultMessage(null);

        try {
            const result = await organizationsRepository.recalculateFilePriorities(
                organizationId!,
                selectedTypes,
                reason,
                RecalculationTrigger.ManualUi
            );

            if (result.success) {
                setStatus("submitted");
                setResultMessage(result.message);
                setReason("");
            } else {
                setStatus("error");
                const failed = result.failures
                    .map((f) => MigrationTypeLabels[f.migrationType] ?? f.migrationType)
                    .join(", ");
                setErrorMessage(
                    `${result.message}${failed ? ` Failed: ${failed}.` : ""}`
                );
            }

            await queryClient.invalidateQueries({
                queryKey: [QUERY_KEYS.MIGRATION_QUEUE_STATUS, organizationId],
            });
        } catch (error: any) {
            setStatus("error");
            setErrorMessage(error.message);
        }
    };

    return (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 items-start">
            <div>
                <div className="flex mt-2 mb-2 gap-2 flex-col sm:flex-row items-start sm:items-center flex-wrap justify-between">
                    <h2 className="text-2xl font-bold tracking-tight flex items-center gap-2">
                        Refresh Files Migration Queue
                        <DocsHelpLink to="how-to/refresh-migration-queue" />
                    </h2>
                </div>

                <p className="text-sm text-muted-foreground mb-4">
                    Refreshes the files migration queue for{" "}
                    <span className="font-medium text-foreground">
                        {organizationName || "this organization"}
                    </span>{" "}
                    by recalculating each file's priority, so files are migrated
                    in the correct order. Run this after a SQL script changes the
                    status of files or their parent entities.
                </p>

                <div className="mb-4 space-y-1.5">
                    <Label className="after:content-['*'] after:ml-0.5 after:text-destructive">
                        Migration Types
                    </Label>
                    <ToggleGroup
                        type="multiple"
                        variant="outline"
                        size="sm"
                        value={selectedTypes}
                        onValueChange={(value) =>
                            setSelectedTypes(value as MigrationType[])
                        }
                        className="w-full my-1 flex-wrap h-auto"
                    >
                        {RECALCULATION_MIGRATION_TYPES.map((type) => (
                            <ToggleGroupItem
                                key={type}
                                value={type}
                                className="flex-1 min-w-fit"
                            >
                                {MigrationTypeLabels[type]}
                            </ToggleGroupItem>
                        ))}
                    </ToggleGroup>
                    <p
                        className={`text-xs ${
                            selectedTypes.length === 0
                                ? "text-destructive"
                                : "text-muted-foreground"
                        }`}
                    >
                        {selectedTypes.length === 0
                            ? "Select at least one migration type."
                            : "Priorities for the selected migration types will be recalculated."}
                    </p>
                </div>

                <div className="mb-4 space-y-1.5">
                    <Label
                        htmlFor="recalc-reason"
                        className="after:content-['*'] after:ml-0.5 after:text-destructive"
                    >
                        Reason
                    </Label>
                    <Input
                        id="recalc-reason"
                        placeholder="Provide a reason for this recalculation (min 10 characters)..."
                        value={reason}
                        onChange={(e) => setReason(e.target.value)}
                        aria-invalid={reason.length > 0 && !isReasonValid}
                    />
                    <p
                        className={`text-xs ${
                            reason.length > 0 && !isReasonValid
                                ? "text-destructive"
                                : "text-muted-foreground"
                        }`}
                    >
                        {reason.length > 0 && !isReasonValid
                            ? "Reason must be at least 10 characters long."
                            : "Describe why priorities need recalculating. This will be logged for audit purposes."}
                    </p>
                </div>

                <div className="flex justify-center">
                    <Button
                        onClick={() => setIsConfirmModalOpen(true)}
                        disabled={!canSubmit}
                    >
                        {status === "loading" && (
                            <Loader2 className="size-4 animate-spin" />
                        )}
                        Recalculate Priorities
                    </Button>
                </div>

                {status === "submitted" && (
                    <Alert className="mt-4 border-emerald-300 dark:border-emerald-700 bg-emerald-50 dark:bg-emerald-950/30">
                        <CheckCircle2 className="size-4 text-emerald-600" />
                        <AlertDescription>
                            {resultMessage ??
                                "Recalculation enqueued. Watch the status panel for progress."}
                        </AlertDescription>
                    </Alert>
                )}

                {status === "error" && errorMessage && (
                    <Alert variant="destructive" className="mt-4">
                        <AlertTriangle className="size-4" />
                        <AlertDescription className="whitespace-pre-line">
                            {errorMessage}
                        </AlertDescription>
                    </Alert>
                )}
            </div>

            <div className="lg:h-[calc(100vh-100px)] flex flex-col">
                <MigrationQueueStatusPanel organizationId={organizationId} />
            </div>

            {isConfirmModalOpen && (
                <ConfirmationModal
                    open={isConfirmModalOpen}
                    onClose={() => setIsConfirmModalOpen(false)}
                    onConfirm={async () => {
                        setIsConfirmModalOpen(false);
                        await handleSubmit();
                    }}
                    message="Are you sure you want to recalculate file priorities for this organization?"
                    statusColor="danger"
                    buttonText="Recalculate"
                >
                    <div className="mt-4 flex flex-col gap-3">
                        <div>
                            <p className="text-sm font-semibold">
                                Target Organization Name:
                            </p>
                            <p className="text-sm text-muted-foreground">
                                {organizationName || "Unknown"}
                            </p>
                        </div>

                        <div>
                            <p className="text-sm font-semibold">
                                Target Organization Id:
                            </p>
                            <p className="text-sm text-muted-foreground">
                                {organizationId || "Unknown"}
                            </p>
                        </div>

                        <div>
                            <p className="text-sm font-semibold">
                                Migration Types:
                            </p>
                            <p className="text-sm text-muted-foreground">
                                {selectedTypes
                                    .map((t) => MigrationTypeLabels[t])
                                    .join(", ")}
                            </p>
                        </div>

                        <div>
                            <p className="text-sm font-semibold">Reason:</p>
                            <div className="p-2 bg-muted rounded-sm border font-mono text-xs whitespace-pre-wrap max-h-24 overflow-auto">
                                {reason}
                            </div>
                        </div>

                        <div className="rounded-md border border-amber-300 dark:border-amber-800 bg-amber-50 dark:bg-amber-950/30 p-3">
                            <p className="text-sm font-semibold flex items-center gap-1.5 text-amber-700 dark:text-amber-400">
                                <AlertTriangle className="size-4" />
                                Before you continue
                            </p>
                            <ul className="mt-1.5 list-disc pl-5 text-xs text-muted-foreground space-y-1">
                                {RECALCULATION_WARNINGS.map((warning, i) => (
                                    <li key={i}>{warning}</li>
                                ))}
                            </ul>
                        </div>
                    </div>
                </ConfirmationModal>
            )}
        </div>
    );
};

export default RecalculateFilePrioritiesPage;
